import React from 'react';
import DistributiveDropdown from '../DistributiveDropdown';
import TrashIcon from '../icons/TrashIcon';
import { AcademicSystem, CourseEntry, CourseFieldKey, MajorRequirement, MinorRequirement, MultiSelectOption, YearPlan } from '../../types';
import { makeYearPlan, termsForSystem } from '../../utils/planner';

type YearTableProps = {
  selection: AcademicSystem;
  yearPlans: YearPlan[];
  majors: MajorRequirement[];
  minors: MinorRequirement[];
  showDistributives: boolean;
  distributiveOptions: MultiSelectOption[];
  onRemoveYear: (yearId: string) => void;
  onAddCourse: (yearId: string, term: string) => void;
  onRemoveCourse: (yearId: string, term: string, courseId: string) => void;
  onUpdateCourse: (
    yearId: string,
    term: string,
    courseId: string,
    field: CourseFieldKey,
    value: CourseEntry[CourseFieldKey]
  ) => void;
  onUpdateTermCompletion: (yearId: string, term: string, completed: boolean) => void;
};

const YearTable: React.FC<YearTableProps> = ({
  selection,
  yearPlans,
  majors,
  minors,
  showDistributives,
  distributiveOptions,
  onRemoveYear,
  onAddCourse,
  onRemoveCourse,
  onUpdateCourse,
  onUpdateTermCompletion,
}) => {
  const buildProgramOptions = (programs: Array<{ name: string; classes: { id: string; name: string }[] }>, prefix: string) =>
    programs.flatMap((program) =>
      program.classes
        .filter((cls) => cls.name.trim() !== '')
        .map((cls) => ({
          id: cls.id,
          label: `${program.name || prefix}: ${cls.name}`,
        }))
    );
  const programClassOptions = [
    ...buildProgramOptions(majors, 'Major'),
    ...buildProgramOptions(minors, 'Minor'),
  ];
  const hasProgramOptions = programClassOptions.length > 0;

  return (
    <div className="overflow-x-auto pb-6">
      <div className="mx-auto w-full min-w-[1040px] max-w-[1700px]">
        <div className="w-full overflow-hidden rounded-2xl border border-slate-200 shadow-[0_12px_50px_-45px_rgba(15,23,42,0.55)]">
          <table className="min-w-full table-fixed border-collapse text-xs md:text-sm">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-600 md:text-xs">
              <tr>
                <th className="border-b border-slate-200 px-3 py-3 text-left">Year</th>
                {termsForSystem(selection).map((term) => (
                  <th key={term} className="border-b border-slate-200 px-3 py-3 text-left">
                    {term}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {yearPlans.map((year) => (
                <tr key={year.id} className="group/yearRow border-t border-slate-100 align-top">
                  <th className="bg-slate-50 px-4 py-4 text-left text-sm font-semibold text-slate-800">
                    <div className="flex flex-col items-start gap-2">
                      <span>{year.label}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${year.label}`}
                        onClick={() => {
                          const confirmed = window.confirm(`Remove ${year.label} from your plan?`);
                          if (confirmed) {
                            onRemoveYear(year.id);
                          }
                        }}
                        className="rounded-full border border-slate-200 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 opacity-0 transition hover:-translate-y-0.5 hover:border-rose-200 hover:text-rose-600 hover:shadow-[0_8px_24px_-16px_rgba(248,113,113,0.45)] focus:opacity-100 focus:outline-none group-hover/yearRow:opacity-100 group-focus-within/yearRow:opacity-100"
                      >
                        Remove
                      </button>
                    </div>
                  </th>
                  {termsForSystem(selection).map((term) => {
                    const termPlan = year.terms.find((t) => t.term === term) ?? makeYearPlan(0, [term]).terms[0];
                    return (
                      <td key={term} className="px-2 py-3 align-top">
                        <div className="group/term relative rounded-lg border border-slate-200 bg-white/80 p-2.5 shadow-inner shadow-slate-100">
                          <div className="flex items-center justify-end">
                            <label className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                              <input
                                type="checkbox"
                                checked={termPlan.completed}
                                onChange={(event) => onUpdateTermCompletion(year.id, termPlan.term, event.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                                aria-label={`${year.label} ${termPlan.term} mark term completed`}
                              />
                              Term completed
                            </label>
                          </div>
                          <div className="mt-2 space-y-3">
                            {termPlan.courses.map((course) => (
                              <div
                                key={course.id}
                                className={`group/courseRow relative flex flex-col items-stretch gap-2 pr-6 md:grid md:items-start md:gap-4 md:pr-8 ${
                                  showDistributives
                                    ? 'md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]'
                                    : 'md:grid-cols-[minmax(0,1fr)_auto]'
                                }`}
                              >
                                <button
                                  type="button"
                                  aria-label={`Remove course from ${year.label} ${termPlan.term}`}
                                  onClick={() => onRemoveCourse(year.id, termPlan.term, course.id)}
                                  className="absolute -right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 opacity-0 shadow-sm transition hover:-translate-y-1/2 hover:border-rose-200 hover:text-rose-600 hover:shadow-[0_8px_24px_-16px_rgba(248,113,113,0.45)] focus:opacity-100 focus:outline-none group-hover/courseRow:opacity-100 group-focus-within/courseRow:opacity-100"
                                >
                                  <TrashIcon className="h-3 w-3" />
                                </button>
                                <div className="relative group/course min-w-0">
                                  {course.isMajorRequirement ? (
                                    <>
                                      <label className="sr-only" htmlFor={`${year.id}-${termPlan.term}-${course.id}-required`}>
                                        {`${year.label} ${termPlan.term} required class`}
                                      </label>
                                      <select
                                        id={`${year.id}-${termPlan.term}-${course.id}-required`}
                                        value={course.requiredCourseId ?? ''}
                                        aria-label={`${year.label} ${termPlan.term} required class`}
                                        onChange={(event) => {
                                          const selected = programClassOptions.find((option) => option.id === event.target.value);
                                          onUpdateCourse(year.id, termPlan.term, course.id, 'requiredCourseId', event.target.value);
                                          onUpdateCourse(year.id, termPlan.term, course.id, 'name', selected?.label ?? course.name);
                                          if (!event.target.value) {
                                            onUpdateCourse(year.id, termPlan.term, course.id, 'isMajorRequirement', false);
                                          }
                                        }}
                                        disabled={!hasProgramOptions}
                                        className="w-full min-w-0 rounded-md border border-slate-200 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500"
                                      >
                                        <option value="">
                                          {hasProgramOptions ? 'Select required class' : 'Add majors/minors to select required classes'}
                                        </option>
                                        {programClassOptions.map((option) => (
                                          <option key={option.id} value={option.id}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </>
                                  ) : (
                                    <>
                                      <label className="sr-only" htmlFor={`${year.id}-${termPlan.term}-${course.id}-name`}>
                                        {`${year.label} ${termPlan.term} course name`}
                                      </label>
                                      <input
                                        id={`${year.id}-${termPlan.term}-${course.id}-name`}
                                        type="text"
                                        value={course.name}
                                        aria-label={`${year.label} ${termPlan.term} course name`}
                                        onChange={(event) =>
                                          onUpdateCourse(year.id, termPlan.term, course.id, 'name', event.target.value)
                                        }
                                        className="w-full min-w-0 rounded-md border border-slate-200 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                      />
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center justify-start md:justify-center md:pt-1">
                                  <label className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                                    <input
                                      type="checkbox"
                                      checked={course.isMajorRequirement}
                                      onChange={(event) => {
                                        const checked = event.target.checked;
                                        onUpdateCourse(year.id, termPlan.term, course.id, 'isMajorRequirement', checked);
                                        if (!checked) {
                                          onUpdateCourse(year.id, termPlan.term, course.id, 'requiredCourseId', '');
                                        }
                                      }}
                                      className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                                      aria-label={`${year.label} ${termPlan.term} mark as required major class`}
                                    />
                                    <span></span>
                                  </label>
                                </div>
                                {showDistributives && (
                                  <div className="flex w-full min-w-0 flex-col gap-1 md:w-full md:justify-self-end">
                                    <DistributiveDropdown
                                      label={`${year.label} ${termPlan.term} distributive`}
                                      options={distributiveOptions}
                                      selected={course.distributives}
                                      onChange={(values) =>
                                        onUpdateCourse(year.id, termPlan.term, course.id, 'distributives', values)
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 flex justify-center">
                            <button
                              type="button"
                              aria-label={`Add course for ${year.label} ${termPlan.term}`}
                              onClick={() => onAddCourse(year.id, termPlan.term)}
                              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 opacity-0 transition duration-200 pointer-events-none group-hover/term:pointer-events-auto group-hover/term:opacity-100 group-focus-within/term:pointer-events-auto group-focus-within/term:opacity-100 hover:-translate-y-0.5 hover:border-slate-900"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default YearTable;
